import { memo, useId, useState } from 'react';
import { useTranslations } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { TransportationAssistanceType } from '@/hooks/trips/types';

export interface TransportExpense {
  amount: string;
  currency: string;
  assistanceType: TransportationAssistanceType;
  needsBookingSupport: boolean;
  arrivalDate?: Date;
  departureDate?: Date;
}

interface TransportExpenseFormProps {
  onSave: (expense: TransportExpense) => void;
  onCancel: () => void;
  initialData?: TransportExpense;
  isLoading?: boolean;
  isInternationalTrip?: boolean;
  cityDates?: {
    arrivalDate: Date;
    departureDate: Date;
  };
}

const ASSISTANCE_OPTIONS: Array<{
  value: TransportationAssistanceType;
  labelKey: string;
}> = [
  { value: 'ticket_or_advance', labelKey: 'transportOptions.ticketOrAdvance' },
  { value: 'rental_car', labelKey: 'transportOptions.rentalCar' },
  { value: 'car_with_driver', labelKey: 'transportOptions.carWithDriver' },
];

function TransportExpenseFormComponent({
  onSave,
  onCancel,
  initialData,
  isLoading = false,
  isInternationalTrip = false,
  cityDates,
}: TransportExpenseFormProps) {
  const t = useTranslations('expenses');
  const initialAssistanceType: TransportationAssistanceType = isInternationalTrip
    ? 'ticket_or_advance'
    : (initialData?.assistanceType ?? 'ticket_or_advance');

  const [formData, setFormData] = useState<TransportExpense>({
    amount: initialData?.amount || '',
    currency: initialData?.currency || 'GEL',
    assistanceType: initialAssistanceType,
    needsBookingSupport: initialData?.needsBookingSupport ?? false,
    arrivalDate: initialData?.arrivalDate || cityDates?.arrivalDate,
    departureDate: initialData?.departureDate || cityDates?.departureDate,
  });

  const radioGroupName = useId();
  const amountInputId = useId();
  const assistanceType = isInternationalTrip ? 'ticket_or_advance' : formData.assistanceType;
  const isTicketOrAdvance = assistanceType === 'ticket_or_advance';

  const requiresAmount = isTicketOrAdvance && !formData.needsBookingSupport;
  const trimmedAmount = formData.amount.trim();
  const isValid = !requiresAmount || trimmedAmount.length > 0;

  const handleSave = () => {
    if (!isValid) {
      return;
    }

    onSave({
      ...formData,
      assistanceType,
      amount: requiresAmount ? trimmedAmount : '',
      needsBookingSupport: isTicketOrAdvance ? formData.needsBookingSupport : false,
    });
  };

  return (
    <div className="rounded-2xl border border-[#ebebeb] bg-white p-5">
      {/* Header with icon and title */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full items-center gap-3 sm:w-auto">
          <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-[#f9f9f9] p-2">
            <img src="/assets/icons/plane.svg" alt="" className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-[15px] leading-6 font-medium text-[#292929]">{t('transport')}</h3>
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isLoading}
            className="h-auto w-full rounded-[1.875rem] px-3.5 py-2.5 text-sm font-medium text-black hover:bg-[#f9f9f9] sm:w-auto"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || isLoading}
            className={`h-auto w-full rounded-[1.875rem] px-3.5 py-2.5 text-sm font-medium transition-all sm:w-auto ${
              isValid && !isLoading
                ? 'bg-[#242424] text-white hover:bg-[#1a1a1a]'
                : 'bg-[#f9f9f9] text-[#bbbbbb]'
            }`}
          >
            {t('save')}
          </Button>
        </div>
      </div>

      {/* Divider */}
      <div className="mb-4 h-px bg-[#f0f0f0]" />

      <div className="mb-4 flex flex-col gap-3">
        {isInternationalTrip ? (
          <div className="flex w-full flex-col gap-4">
            <label className="flex items-center gap-3 text-[0.9375rem] leading-6 text-[#292929]">
              <Checkbox
                checked={formData.needsBookingSupport}
                onCheckedChange={(checked) =>
                  setFormData((previous) => ({
                    ...previous,
                    needsBookingSupport: checked === true,
                    amount: checked === true ? '' : previous.amount,
                  }))
                }
                className="h-5 w-5"
              />
              {t('needsTicketHelp')}
            </label>

            {!formData.needsBookingSupport ? (
              <div className="flex flex-col">
                <div className="pb-2">
                  <label htmlFor={amountInputId} className="text-sm font-medium text-[#292929]">
                    {t('totalAmount')}
                  </label>
                </div>
                <div className="relative">
                  <div
                    className="flex h-14 items-center rounded-2xl border border-[#ebebeb] bg-white px-4 py-2 transition-colors focus-within:border-[#292929] hover:border-[#292929]"
                    onClick={() => document.getElementById(amountInputId)?.focus()}
                  >
                    <div className="relative w-full">
                      <input
                        id={amountInputId}
                        value={formData.amount ? `₾${formData.amount}` : ''}
                        onChange={(event) => {
                          const value = event.target.value
                            .replace(/[^₾0-9.]/g, '')
                            .replace('₾', '');
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            setFormData((previous) => ({ ...previous, amount: value }));
                          }
                        }}
                        placeholder=" "
                        className={`peer h-10 w-full border-none bg-transparent p-0 pt-4 text-base leading-6 outline-none ${
                          formData.amount ? 'text-transparent caret-[#292929]' : 'text-[#292929]'
                        }`}
                      />
                      <label
                        htmlFor={amountInputId}
                        className="pointer-events-none absolute left-0 origin-left text-[#666666] transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:leading-6 peer-focus:top-0 peer-focus:translate-y-0 peer-focus:text-[0.8125rem] peer-focus:leading-4 peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[0.8125rem] peer-[:not(:placeholder-shown)]:leading-4"
                      >
                        {t('amount')}
                      </label>
                      {formData.amount && (
                        <div className="pointer-events-none absolute top-4 left-0 text-base leading-6 text-[#292929]">
                          ₾{formData.amount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          ASSISTANCE_OPTIONS.map((option) => {
            const optionId = `${radioGroupName}-${option.value}`;
            const isSelected = formData.assistanceType === option.value;
            const showExtras = isSelected && option.value === 'ticket_or_advance';

            return (
              <div key={option.value} className="flex flex-col gap-3">
                <label
                  htmlFor={optionId}
                  className={`flex min-w-0 cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                    isSelected
                      ? 'border-[#292929] bg-[#f9f9f9]'
                      : 'border-[#ebebeb] bg-white hover:border-[#d9d9d9]'
                  }`}
                >
                  <input
                    id={optionId}
                    type="radio"
                    name={radioGroupName}
                    value={option.value}
                    checked={isSelected}
                    onChange={() =>
                      setFormData((previous) => ({
                        ...previous,
                        assistanceType: option.value,
                        needsBookingSupport:
                          option.value === 'ticket_or_advance'
                            ? previous.needsBookingSupport
                            : false,
                        amount: option.value === 'ticket_or_advance' ? previous.amount : '',
                      }))
                    }
                    className="sr-only"
                  />
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                      isSelected ? 'border-[#242424]' : 'border-[#d4d4d4]'
                    }`}
                    aria-hidden="true"
                  >
                    <span
                      className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                        isSelected ? 'bg-[#242424]' : 'bg-transparent'
                      }`}
                    />
                  </span>
                  <span className="min-w-0 flex-1 text-[0.9375rem] leading-6 text-[#292929]">
                    {t(option.labelKey)}
                  </span>
                </label>

                {showExtras ? (
                  <div className="flex w-full flex-col gap-4 rounded-2xl border border-[#ebebeb] bg-white px-4 py-4">
                    <label className="flex items-center gap-3 text-[0.9375rem] leading-6 text-[#292929]">
                      <Checkbox
                        checked={formData.needsBookingSupport}
                        onCheckedChange={(checked) =>
                          setFormData((previous) => ({
                            ...previous,
                            needsBookingSupport: checked === true,
                            amount: checked === true ? '' : previous.amount,
                          }))
                        }
                        className="h-5 w-5"
                      />
                      {t('needsTicketHelp')}
                    </label>

                    {!formData.needsBookingSupport ? (
                      <div className="flex flex-col">
                        <div className="pb-2">
                          <label
                            htmlFor={amountInputId}
                            className="text-sm font-medium text-[#292929]"
                          >
                            {t('totalAmount')}
                          </label>
                        </div>
                        <div className="relative">
                          <div
                            className="flex h-14 items-center rounded-2xl border border-[#ebebeb] bg-white px-4 py-2 transition-colors focus-within:border-[#292929] hover:border-[#292929]"
                            onClick={() => document.getElementById(amountInputId)?.focus()}
                          >
                            <div className="relative w-full">
                              <input
                                id={amountInputId}
                                value={formData.amount ? `₾${formData.amount}` : ''}
                                onChange={(event) => {
                                  const value = event.target.value
                                    .replace(/[^₾0-9.]/g, '')
                                    .replace('₾', '');
                                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                    setFormData((previous) => ({ ...previous, amount: value }));
                                  }
                                }}
                                placeholder=" "
                                className={`peer h-10 w-full border-none bg-transparent p-0 pt-4 text-base leading-6 outline-none ${
                                  formData.amount
                                    ? 'text-transparent caret-[#292929]'
                                    : 'text-[#292929]'
                                }`}
                              />
                              <label
                                htmlFor={amountInputId}
                                className="pointer-events-none absolute left-0 origin-left text-[#666666] transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:leading-6 peer-focus:top-0 peer-focus:translate-y-0 peer-focus:text-[0.8125rem] peer-focus:leading-4 peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[0.8125rem] peer-[:not(:placeholder-shown)]:leading-4"
                              >
                                {t('amount')}
                              </label>
                              {formData.amount && (
                                <div className="pointer-events-none absolute top-4 left-0 text-base leading-6 text-[#292929]">
                                  ₾{formData.amount}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const TransportExpenseForm = memo(TransportExpenseFormComponent);

export default TransportExpenseForm;
